import React from "react";
import { motion } from "framer-motion";
import { Calendar, MapPin, CheckCircle2 } from "lucide-react";
import { COLOR } from "../../constants/colors";

interface SuccessModalProps {
  open: boolean;
  onClose: () => void;
  selection: { day: string; time: string; dateLabel: string } | null;
  locationName?: string;
  locationAddress?: string;
}

export default function SuccessModal({
  open,
  onClose,
  selection,
  locationName = "3ZERO3",
  locationAddress = "303 Battery St, Seattle, WA 98121",
}: SuccessModalProps) {
  if (!open || !selection) return null;

  const needToKnow = [
    "You can message the seller 30 minutes before your visit if you have questions.",
    "The seller will meet you at the building entrance.",
    "Please arrive on time to keep visits on schedule.",
  ];

  // Helper function to parse time string and create calendar date format
  const parseDateTime = () => {
    // selection.day is in format "2025-11-22"
    // selection.time is in format "3:00 PM"

    const [year, month, day] = selection.day.split("-");
    const timeStr = selection.time;

    // Parse time (e.g., "3:00 PM")
    const [timepart, period] = timeStr.split(" ");
    const [hours, minutes] = timepart.split(":");
    let hour = parseInt(hours);

    // Convert to 24-hour format
    if (period === "PM" && hour !== 12) hour += 12;
    if (period === "AM" && hour === 12) hour = 0;

    const hourStr = hour.toString().padStart(2, "0");
    const minuteStr = minutes.padStart(2, "0");

    // Format: YYYYMMDDTHHMMSS
    const startDateTime = `${year}${month}${day}T${hourStr}${minuteStr}00`;

    // End time is 1 hour later
    const endHour = (hour + 1).toString().padStart(2, "0");
    const endDateTime = `${year}${month}${day}T${endHour}${minuteStr}00`;

    return { startDateTime, endDateTime };
  };

  const generateGoogleCalendarUrl = () => {
    const title = encodeURIComponent("[Tili] All must go — discounts on all items and furniture!");

    // Format the need to know items for calendar details
    const needToKnowText = needToKnow
      .map((item, index) => `${index + 1}. ${item}`)
      .join("%0A");

    const details = encodeURIComponent(
      `Visit Time: ${selection.dateLabel} at ${selection.time}

Location: ${locationName}, ${locationAddress}

Before You Go:
${needToKnow.map((item, index) => `${index + 1}. ${item}`).join("\n")}`
    );
    const location = encodeURIComponent(`${locationName}, ${locationAddress}`);

    const { startDateTime, endDateTime } = parseDateTime();
    const dates = `${startDateTime}/${endDateTime}`;

    return `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${title}&details=${details}&location=${location}&dates=${dates}`;
  };

  const generateICalFile = () => {
    // Format the need to know items for iCal description
    const needToKnowText = needToKnow
      .map((item, index) => `${index + 1}. ${item}`)
      .join("\\n");

    const { startDateTime, endDateTime } = parseDateTime();

    const description = `Visit Time: ${selection.dateLabel} at ${selection.time}\\n\\nLocation: ${locationName}, ${locationAddress}\\n\\nBefore You Go:\\n${needToKnowText}`;

    const icsContent = `BEGIN:VCALENDAR
                        VERSION:2.0
                        BEGIN:VEVENT
                        SUMMARY:[Tili] All must go — discounts on all items and furniture!
                        DESCRIPTION:${description}
                        LOCATION:${locationName}, ${locationAddress}
                        DTSTART:${startDateTime}
                        DTEND:${endDateTime}
                        END:VEVENT
                        END:VCALENDAR`;

    const blob = new Blob([icsContent], { type: "text/calendar" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "tili-visit.ics";
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-3xl shadow-xl w-full max-w-md relative"
        style={{ 
          maxHeight: "95vh", 
          overflowY: "auto",
          scrollbarWidth: "none",
          msOverflowStyle: "none",
        }}
      >
        <style>{`
          .bg-white.rounded-3xl::-webkit-scrollbar {
            display: none;
          }
        `}</style>
        <button
          className="absolute top-4 right-4 text-2xl font-bold hover:opacity-70 transition"
          onClick={onClose}
          aria-label="Close"
          style={{ color: COLOR.subtext }}
        >
          ×
        </button>

        <div className="p-6">
          <div className="flex justify-center mb-4">
            <div
              className="w-20 h-20 rounded-full flex items-center justify-center"
              style={{ backgroundColor: "#E8F9F0" }}
            >
              <CheckCircle2
                className="w-12 h-12"
                style={{ color: "#10B981" }}
              />
            </div>
          </div>

          <h2
            className="text-2xl font-extrabold mb-2 text-center"
            style={{ color: COLOR.text }}
          >
            You're all set for your visit!
          </h2>
          <p
            className="text-sm mb-6 text-center"
            style={{ color: COLOR.subtext }}
          >
            We've sent the details to your email.
          </p>

          <div
            className="mb-4 p-4 rounded-2xl"
            style={{
              backgroundColor: COLOR.bg,
              border: "2px solid " + COLOR.border,
            }}
          >
            <div className="flex items-center gap-2 mb-2">
              <Calendar className="h-5 w-5" style={{ color: COLOR.main }} />
              <p className="font-extrabold" style={{ color: COLOR.text }}>
                When
              </p>
            </div>
            <p className="text-sm font-bold" style={{ color: COLOR.text }}>
              {selection.dateLabel}
            </p>
            <p className="text-sm font-bold" style={{ color: COLOR.main }}>
              {selection.time}
            </p>
          </div>

          <div
            className="mb-4 p-4 rounded-2xl"
            style={{
              backgroundColor: COLOR.bg,
              border: "2px solid " + COLOR.border,
            }}
          >
            <div className="flex items-center gap-2 mb-2">
              <MapPin className="h-5 w-5" style={{ color: COLOR.main }} />
              <p className="font-extrabold" style={{ color: COLOR.text }}>
                Where
              </p>
            </div>
            <p className="text-sm font-bold" style={{ color: COLOR.text }}>
              {locationName}
            </p>
            <p className="text-sm font-bold" style={{ color: COLOR.text }}>
              {locationAddress}
            </p>
            <a
              href={`https://maps.google.com/?q=${encodeURIComponent(
                locationAddress
              )}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-xs mt-2 hover:opacity-70 transition"
              style={{ color: COLOR.main }}
            >
              Open in Maps →
            </a>
          </div>

          <div
            className="mb-6 p-4 rounded-2xl"
            style={{
              backgroundColor: COLOR.bg,
              border: "2px solid " + COLOR.border,
            }}
          >
            <p className="font-extrabold mb-2" style={{ color: COLOR.text }}>
              Before You Go
            </p>
            <ul className="text-xs space-y-1.5" style={{ color: COLOR.text }}>
              {needToKnow.map((item, index) => (
                <li key={index} className="flex items-start gap-2">
                  <span style={{ color: COLOR.main }}>•</span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="space-y-3 mb-4">
            <button
              onClick={() => window.open(generateGoogleCalendarUrl(), "_blank")}
              className="w-full flex items-center justify-center gap-2 py-3 px-4 rounded-xl font-extrabold text-sm transition hover:opacity-90"
              style={{
                backgroundColor: COLOR.main,
                border: "2px solid " + COLOR.mainBorder,
                color: "white",
              }}
            >
              <Calendar className="h-5 w-5" />
              Add to Google Calendar
            </button>
            <button
              onClick={generateICalFile}
              className="w-full flex items-center justify-center gap-2 py-3 px-4 rounded-xl font-extrabold text-sm transition hover:opacity-70"
              style={{
                backgroundColor: COLOR.bg,
                border: "2px solid " + COLOR.border,
                color: COLOR.text,
              }}
            >
              <Calendar className="h-5 w-5" />
              Add to Calendar (iCal, Outlook)
            </button>
          </div>

          <button
            onClick={onClose}
            className="w-full py-3 px-4 rounded-xl font-semibold text-sm transition hover:opacity-70"
            style={{ color: COLOR.text }}
          >
            Got it
          </button>
        </div>
      </motion.div>
    </div>
  );
}
